import { useEffect, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useProfile, candidateSections, type Certification, type Education, type Experience } from '@/hooks/use-profile'
import { matchesProfileSkillName } from '@/lib/profile-evidence-display'

import type { SkillEvidenceLinkType } from './skill-manager-row'

function mergeRelatedSkills(existing: string[] | undefined, add: string): string[] {
  const base = [...(existing ?? [])]
  const trimmed = add.trim()
  if (!trimmed) return base
  if (base.some((s) => matchesProfileSkillName(trimmed, s))) return base
  return [...base, trimmed]
}

function formatExperienceOptionLabel(experience: Experience): string {
  const company = experience.company?.trim() || 'Unknown company'
  return `${experience.title.trim()} @ ${company}`
}

function formatEducationOptionLabel(education: Education): string {
  return `${education.degree.trim()} @ ${education.institution.trim()}`
}

function formatCertificationOptionLabel(certification: Certification): string {
  return `${certification.name.trim()} @ ${certification.issuer.trim()}`
}

const LINK_DIALOG_COPY: Record<
  SkillEvidenceLinkType,
  {
    title: string
    fieldLabel: string
    none: string
    allLinked: string
    submit: string
  }
> = {
  experience: {
    title: 'Link to experience',
    fieldLabel: 'Experience',
    none: 'You don\u2019t have any experience entries yet. Add one on your profile first.',
    allLinked: 'Every experience entry already lists this skill.',
    submit: 'Link',
  },
  education: {
    title: 'Link to education',
    fieldLabel: 'Education',
    none: 'You don\u2019t have any education entries yet. Add one on your profile first.',
    allLinked: 'Every education entry already lists this skill.',
    submit: 'Link',
  },
  certification: {
    title: 'Link to certification',
    fieldLabel: 'Certification',
    none: 'You don\u2019t have any certifications yet. Add one on your profile first.',
    allLinked: 'Every certification already lists this skill.',
    submit: 'Link',
  },
}

export function LinkSkillEvidenceDialog({
  open,
  onOpenChange,
  skillName,
  type,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  skillName: string
  type: SkillEvidenceLinkType
}) {
  const {
    profile,
    addExperienceSkillsAsync,
    updateEducationAsync,
    updateCertificationAsync,
    refetch,
  } = useProfile()
  const candidate = candidateSections(profile)
  const skillPayload = skillName.trim()
  const copy = LINK_DIALOG_COPY[type]

  const { options, totalCount } = useMemo(() => {
    if (type === 'experience') {
      const all = candidate.experiences ?? []
      const eligible = all.filter(
        (experience) =>
          !(experience.skills || []).some((skill) => matchesProfileSkillName(skillPayload, skill))
      )
      return {
        totalCount: all.length,
        options: eligible
          .sort((a, b) => (b.start_date || '').localeCompare(a.start_date || ''))
          .map((experience) => ({
            id: experience.id,
            label: formatExperienceOptionLabel(experience),
          })),
      }
    }

    if (type === 'education') {
      const all = (candidate.education ?? []).filter((edu): edu is Education & { id: string } =>
        Boolean(edu.id)
      )
      const eligible = all.filter(
        (edu) => !(edu.related_skills || []).some((s) => matchesProfileSkillName(skillPayload, s))
      )
      return {
        totalCount: all.length,
        options: eligible
          .sort((a, b) => (b.start_date || '').localeCompare(a.start_date || ''))
          .map((edu) => ({
            id: edu.id!,
            label: formatEducationOptionLabel(edu),
          })),
      }
    }

    const all = (candidate.certifications ?? []).filter((cert): cert is Certification & { id: string } =>
      Boolean(cert.id)
    )
    const eligible = all.filter(
      (cert) => !(cert.related_skills || []).some((s) => matchesProfileSkillName(skillPayload, s))
    )
    return {
      totalCount: all.length,
      options: eligible
        .sort((a, b) => (b.issued_at || '').localeCompare(a.issued_at || ''))
        .map((cert) => ({
          id: cert.id!,
          label: formatCertificationOptionLabel(cert),
        })),
    }
  }, [candidate.experiences, candidate.education, candidate.certifications, skillPayload, type])

  const emptyMessage = totalCount === 0 ? copy.none : copy.allLinked

  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setSelectedIds(new Set())
    setSubmitError(null)
  }, [open, skillName, type])

  const setOptionSelected = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const handleOpenChange = (next: boolean) => {
    if (!isSaving) onOpenChange(next)
  }

  const handleLink = async () => {
    if (selectedIds.size === 0 || !skillPayload) return
    setSubmitError(null)
    setIsSaving(true)
    try {
      const ids = [...selectedIds]
      for (const id of ids) {
        if (type === 'experience') {
          await addExperienceSkillsAsync({
            experienceId: id,
            skillNames: [skillPayload],
          })
        } else if (type === 'education') {
          const row = candidate.education?.find((edu) => edu.id === id)
          if (!row?.id) continue
          await updateEducationAsync({
            id: row.id,
            updates: {
              related_skills: mergeRelatedSkills(row.related_skills, skillPayload),
            },
          })
        } else {
          const row = candidate.certifications?.find((cert) => cert.id === id)
          if (!row?.id) continue
          await updateCertificationAsync({
            id: row.id,
            updates: {
              related_skills: mergeRelatedSkills(row.related_skills, skillPayload),
            },
          })
        }
      }
      await refetch()
      onOpenChange(false)
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : `Could not link ${copy.fieldLabel.toLowerCase()} to this skill`
      )
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} modal={false}>
      <DialogContent
        stacked
        className="max-h-[90dvh] max-w-sm gap-4 overflow-hidden"
        onCloseAutoFocus={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>
            Choose one or more places to link{' '}
            <span className="font-medium text-foreground">{skillPayload || 'this skill'}</span>.
          </DialogDescription>
        </DialogHeader>

        {options.length === 0 ? (
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        ) : (
          <ul
            aria-label={copy.fieldLabel}
            className="min-h-0 max-h-[min(60dvh,18rem)] space-y-1 overflow-y-auto overscroll-contain rounded-md border p-1 custom-scrollbar"
          >
            {options.map((option) => {
              const checked = selectedIds.has(option.id)
              const inputId = `link-skill-${type}-${option.id}`
              return (
                <li key={option.id}>
                  <label
                    htmlFor={inputId}
                    className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 hover:bg-accent"
                  >
                    <Checkbox
                      id={inputId}
                      checked={checked}
                      disabled={isSaving}
                      onCheckedChange={(value) => setOptionSelected(option.id, value === true)}
                      aria-label={`Link to ${option.label}`}
                    />
                    <span className="min-w-0 flex-1 truncate text-sm leading-snug">{option.label}</span>
                  </label>
                </li>
              )
            })}
          </ul>
        )}

        {submitError ? (
          <p className="shrink-0 text-sm text-destructive" role="alert">
            {submitError}
          </p>
        ) : null}

        <DialogFooter className="shrink-0">
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleLink()}
            disabled={isSaving || selectedIds.size === 0 || options.length === 0}
          >
            {isSaving ? 'Linking…' : copy.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
